/**
 * Membership: which piece of a chain split each of a room's children lands in.
 *
 * Sides are 1 (`front`, the LEFT of the chain's travel) and -1 (`back`, the
 * RIGHT), as `cutRoomAlongChain` names its pieces. Every rule is "assign by a
 * POINT the element already owns" — a centre, an anchor, a resolved divider
 * vertex — answered by the cut's `sideOf`. Nothing is moved and nothing is
 * deleted: an object whose footprint crosses the chain is placed by its centre
 * and REPORTED (`straddling`), the same contract the merge uses for `stale`,
 * and the collision-warnings pill is what surfaces it to the user.
 */

const { centroidXZ } = require('../room-merge/geometry');
const { planOverlapArea } = require('./overlap');
const { IN_PIECE_TOL_M, pieceHoldingPoint } = require('./pointPiece');
const {
  resolveDividerVertices,
  wallsByIdFromRoom,
} = require('../ceiling-edit/divider-geometry');

/**
 * The piece a point belongs to (`pointPiece.js`, the rule the estimate reads
 * back with): in or on a piece, else the nearer piece; a tie, or no point at
 * all, goes to `fallbackSide` (the piece that keeps the room's id).
 */
function pieceOf(p, cut, fallbackSide) {
  return pieceHoldingPoint(
    p,
    [
      { key: 1, ring: cut.left.polygon },
      { key: -1, ring: cut.right.polygon },
    ],
    fallbackSide
  );
}

const into = (front, back, side, el) => (side === -1 ? back : front).push(el);

/**
 * Split a list of positioned elements across the cut.
 *
 * @returns {{ front: object[], back: object[], straddling: string[] }}
 */
function assignByCenter(elements, cut, fallbackSide = 1) {
  const front = [];
  const back = [];
  const straddling = [];
  for (const el of elements || []) {
    if (!el) continue;
    const p = el.center || el.position;
    // Nothing to place it by: never drop it — park it in the piece that keeps
    // the room's id.
    if (p && cut.straddles(el)) straddling.push(el.id);
    into(front, back, pieceOf(p, cut, fallbackSide), el);
  }
  return { front, back, straddling };
}

/**
 * Openings, which differ from objects in one way: an opening in the CHAIN
 * itself is shared by the two new rooms exactly as a door on any structure
 * wall shared by two rooms is — it lands in BOTH rooms' arrays, each copy
 * re-parented to that room's own face of the wall.
 *
 * Most room copies of an opening are SLIM (`{ id, structureOpeningId,
 * parentWallIds }` — 1069 of 1169 in the corpus): the structure copy holds the
 * centre. `centerOf` lets the caller resolve it; failing that, a copy follows
 * its parent wall (`parentSideOf`), and failing that it is parked on
 * `fallbackSide`. The copies themselves are written back as they came.
 *
 * @param {object[]} openings
 * @param {object} cut a `cutRoomAlongChain` result
 * @param {{ chainFaceIds: Set<string>,
 *           faceFor: (side: 1|-1, point: {x,z}|null, opening) => string|undefined,
 *           fallbackSide?: 1|-1, genId?: () => string,
 *           centerOf?: (opening) => {x,z}|null,
 *           parentSideOf?: (roomWallId: string) => 1|-1|undefined }} opts
 * @returns {{ front: object[], back: object[], shared: string[] }}
 */
function assignOpenings(openings, cut, opts) {
  const {
    chainFaceIds,
    faceFor,
    fallbackSide = 1,
    genId,
    centerOf = (op) => op.center || op.position || null,
    parentSideOf = () => undefined,
  } = opts || {};
  const front = [];
  const back = [];
  const shared = [];
  for (const op of openings || []) {
    if (!op) continue;
    const parent = op.parentWallIds?.[0];
    const p = centerOf(op);
    // On the chain: hung on one of its faces, or centred in its body.
    const onChain =
      (parent && chainFaceIds?.has(parent)) || (p ? cut.inWall(p) : false);
    if (onChain) {
      shared.push(op.id);
      // Two rooms, two COPIES, and they must not share a copy id: every room
      // opening in the corpus has its own (301 shared openings, 301 distinct
      // ids, zero duplicates anywhere), and the 3D scene keys a mesh by it
      // (`mesh.uuid = element.id`), so a duplicate collides. They keep one
      // `structureOpeningId` between them, which is the identity the schedule
      // and the estimate collapse on -- so this stays ONE door, counted once,
      // listed against both rooms.
      const canonical = op.structureOpeningId || op.id;
      front.push({
        ...op,
        structureOpeningId: canonical,
        parentWallIds: [faceFor(1, p, op)].filter(Boolean),
      });
      back.push({
        ...op,
        id: genId ? genId() : `${op.id}_b`,
        structureOpeningId: canonical,
        parentWallIds: [faceFor(-1, p, op)].filter(Boolean),
      });
      continue;
    }
    const side = p
      ? pieceOf(p, cut, fallbackSide)
      : (parent && parentSideOf(parent)) || fallbackSide;
    into(front, back, side, op);
  }
  return { front, back, shared };
}

const plateRing = (plate) =>
  (plate?.polygonCorners || []).map((p) => ({ x: p.x, z: p.z }));

/** A plate overlapping a piece by less than a membership-tolerance square
 *  only touches it. */
const PLATE_TOUCH_M2 = IN_PIECE_TOL_M * IN_PIECE_TOL_M;

/**
 * A room's EXTRA floor plates — everything after the base floor: a raised
 * split-level slab (`_splitLevelRaised`), a stair-landing slab. Each goes,
 * whole and as it was, to the piece holding its centroid, exactly as a floor
 * hole does. One the chain runs across is never clipped (the slab's own edges
 * are measured, a cut through it is not) and never dropped: it stays whole on
 * its centroid's piece and is REPORTED (`straddling`).
 *
 * @returns {{ front: object[], back: object[], straddling: string[] }}
 */
function assignFloorPlates(plates, cut, fallbackSide = 1) {
  const front = [];
  const back = [];
  const straddling = [];
  for (const plate of plates || []) {
    if (!plate) continue;
    const ring = plateRing(plate);
    if (ring.length < 3) {
      into(front, back, fallbackSide, plate);
      continue;
    }
    const side = pieceOf(centroidXZ(ring), cut, fallbackSide);
    const inLeft = planOverlapArea(ring, cut.left.polygon) > PLATE_TOUCH_M2;
    const inRight = planOverlapArea(ring, cut.right.polygon) > PLATE_TOUCH_M2;
    if (inLeft && inRight) straddling.push(plate.id);
    into(front, back, side, plate);
  }
  return { front, back, straddling };
}

/** Callouts follow their anchor point. */
function assignCallouts(callouts, cut, fallbackSide = 1) {
  const front = [];
  const back = [];
  for (const co of callouts || []) {
    if (!co) continue;
    into(front, back, pieceOf(co.anchor, cut, fallbackSide), co);
  }
  return { front, back };
}

/**
 * Split one room's `structure.ceilingEdits` entry across the cut. Dividers go
 * by the midpoint of their resolved vertices, regions by their anchor. Inverse
 * of `mergeCeilingEditsForRoomMerge`; anything that fails to resolve degrades
 * the same way it does there — it is dropped at generation time, not here.
 *
 * @returns {{ front: object|null, back: object|null }}
 */
function splitCeilingEdits(spec, room, cut, fallbackSide = 1) {
  if (!spec || typeof spec !== 'object') return { front: null, back: null };
  const wallsById = wallsByIdFromRoom(room);
  const buckets = {
    1: { dividers: [], regions: [] },
    '-1': { dividers: [], regions: [] },
  };

  for (const d of Array.isArray(spec.dividers) ? spec.dividers : []) {
    const pts = resolveDividerVertices(d, wallsById);
    if (!pts || pts.length === 0) continue; // unresolvable — drops, as in a merge
    const mid = pts[Math.floor(pts.length / 2)];
    buckets[pieceOf(mid, cut, fallbackSide) === -1 ? -1 : 1].dividers.push(d);
  }
  for (const r of Array.isArray(spec.regions) ? spec.regions : []) {
    const a = r?.anchor;
    if (!a || typeof a.x !== 'number' || typeof a.z !== 'number') continue;
    buckets[pieceOf(a, cut, fallbackSide) === -1 ? -1 : 1].regions.push(r);
  }

  const shape = (b) =>
    b.dividers.length || b.regions.length
      ? { version: spec.version || 1, dividers: b.dividers, regions: b.regions }
      : null;
  return { front: shape(buckets[1]), back: shape(buckets[-1]) };
}

/**
 * Links whose endpoints all landed on the same side follow their elements;
 * one spanning the cut is dropped and reported — it described a relationship
 * between two rooms, which the link graph has no node for.
 *
 * @param {Map<string, 1|-1>} sideById id → side, for every assigned element
 * @returns {{ front: object[], back: object[], dropped: string[] }}
 */
function splitLinks(links, sideById, fallbackSide = 1) {
  const front = [];
  const back = [];
  const dropped = [];
  for (const link of links || []) {
    if (!link) continue;
    const endpoints = Array.isArray(link.endpoints)
      ? link.endpoints
      : [link.a, link.b].filter(Boolean);
    const sides = new Set();
    for (const ep of endpoints) {
      const id =
        typeof ep === 'string' ? ep : ep?.id || ep?.fixtureGroupId || ep?.bayId;
      const s = id != null ? sideById.get(id) : undefined;
      if (s !== undefined) sides.add(s);
    }
    if (sides.size > 1) {
      dropped.push(link.id);
      continue;
    }
    if (sides.size === 0) {
      into(front, back, fallbackSide, link);
      continue;
    }
    into(front, back, sides.has(-1) ? -1 : 1, link);
  }
  return { front, back, dropped };
}

module.exports = {
  pieceOf,
  assignByCenter,
  assignOpenings,
  assignFloorPlates,
  assignCallouts,
  splitCeilingEdits,
  splitLinks,
};
